import { CommonModule } from '@angular/common';
import {
    Component, inject, Input, OnInit, Signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { ApiComingSoonBackSoonService } from '@api/_api/api-coming-soon-back-soon/api-coming-soon-back-soon.service';
import { ComingSoonBackSoonRequestBody } from '@api/coming-soon-back-soon/coming-soon-back-soon.models';
import { ComingSoonBackSoonService } from '@api/coming-soon-back-soon/coming-soon-back-soon.service';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { SidebarPublicPort } from '@features/sidebar';
import { NotificationService, PublicNotificationTypeEnum } from '@layout/ui/components/notification';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { EventFormErrorAnalytics, SubscribeComingSoonBackSoonAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { ProductViewPlaceValue } from '@shared/product/models';
import { RelatedProductsComponent } from '@shared/product/related-products';
import { InputTermsSections } from '@shared/terms-conditions/components/input-terms-conditions/input-terms-conditions.component';
import { AcceptanceService } from '@shared/terms-conditions/services/acceptance.service';
import { TermsConditionsModule } from '@shared/terms-conditions/terms-conditions.module';

const EMAIL_REGEX = RegExp(/^(([^\s"(),.:;<>@[\]]+(\.[^\s"(),.:;<>@[\]]+)*)|(".+"))@(([^\s"(),.:;<>@[\]]+\.)+[^\s"(),.:;<>@[\]]{2,})$/i);

interface RelatedProductsViewModel {
    place: ProductViewPlaceValue;
    baseProducts: ProductPublicModels.PublicProduct[];
    hidePriceText: boolean;
    slidesPerView: number;
}

@Component({
    selector: 'product-coming-soon',
    templateUrl: './product-coming-soon.component.html',
    providers: [
        ComingSoonBackSoonService,
        ApiComingSoonBackSoonService,
    ],
    imports: [
        TermsConditionsModule,
        CommonModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        RelatedProductsComponent,
    ],
    standalone: true,
})
export class ProductComingSoonComponent extends MarketConfigurable<Record<string, boolean>> implements OnInit {
    // #region DEPENDENCIES
    private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);

    private readonly notificationService = inject(NotificationService);

    private readonly comingSoonBackSoonService: ComingSoonBackSoonService = inject(ComingSoonBackSoonService);

    private readonly acceptanceService: AcceptanceService = inject(AcceptanceService);

    private readonly store: Store = inject(Store);
    // #endregion DEPENDENCIES

    // #region INPUTS
    @Input() public product: ProductPublicModels.PublicProduct;

    @Input() public countryCode: string;
    // #endregion INPUTS

    private readonly storeAndLanguage$$: Signal<StoreAndLanguage> = this.store.selectSignal(getStoreAndLanguage);

    public form: UntypedFormGroup;

    public stockAvailabilitySuccess = false;

    public isActiveTerms = false;

    public displayRelatedProducts = false;

    public termsConditionsSections: InputTermsSections[];

    public relatedProductsVm: RelatedProductsViewModel;

    private kind: string;

    protected readonly config: Record<string, Record<string, boolean>> = {
        CN: {
            showSensitiveDataConditions: true,
        },
        default: {
            showSensitiveDataConditions: false,
        },
    };

    // biome-ignore lint/complexity/noUselessConstructor: Required due it's a herhited class
    constructor() {
        super();
    }

    public ngOnInit(): void {
        this.kind = this.product.status.selectedSize.visibilityValue.toLowerCase();
        this.initForm();

        this.termsConditionsSections = this.generateTermsConditionsSections();

        this.relatedProductsVm = {
            place: ProductViewPlaceValue.CS_BS,
            baseProducts: [this.product],
            hidePriceText: true,
            slidesPerView: 2.5,
        };
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            email: new UntypedFormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
        });

        this.displayRelatedProducts = true;
    }

    private generateTermsConditionsSections(): InputTermsSections[] {
        const sections = [];

        const configTerms = this.getConfigByMarketCode(this.countryCode);

        sections.push(
            { type: 'productStatusConditions', required: true }
        );

        if (configTerms.showSensitiveDataConditions) {
            sections.push(
                { type: 'sensitiveDataConditions', required: true }
            );
        }

        return sections;
    }

    public addFormControl(name: string, formGroup: UntypedFormGroup): void {
        if (this.form.controls[name]) {
            this.form.removeControl(name, { emitEvent: false });
        }
        this.form.addControl(name, formGroup, { emitEvent: false });
    }

    public close(): void {
        SidebarPublicPort(this.store).closeSidebar();
        this.displayRelatedProducts = false;
    }

    public changeEmail(): void {
        if (this.form.controls.email.invalid) {
            this.store.dispatch(EventFormErrorAnalytics({
                formName: `${this.kind}_form`,
                form: [{
                    name: 'email',
                    value: this.form.controls.email.value,
                    valid: this.form.controls.email.valid,
                }],
                eventFrom: EventFrom.comingSoon,
            }));
        }
    }

    public submit(): void {
        const email = this.form.value.email;
        const suscriptionData: ComingSoonBackSoonRequestBody = {
            email,
            quantity: 1,
            sku: +this.product.status.selectedSize.sku,
        };

        if (this.isActiveTerms) {
            this.acceptanceService.consent('COMING_SOON', ['transferenciaforzosa'], email, this.storeAndLanguage$$().storeId);
        }

        this.comingSoonBackSoonService.subscribeTocomingSoonBackSoon(this.product.id, suscriptionData).pipe(
            take(1)
        ).subscribe(() => {
            this.notificationService.openNotification({
                title: 'dev.product.comingSoon.added',
                type: PublicNotificationTypeEnum.TOAST,
            });

            this.store.dispatch(SubscribeComingSoonBackSoonAnalytics({
                product: this.product,
            }));

            this.close();
        });
    }
}
